// src/components/admin/EditAdminModal.js
import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import EmailVerificationBadge from '../common/EmailVerificationBadge';

/**
 * EditAdminModal Component
 * Modal for editing admin user (client_admin or core_admin) information
 *
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Function to close modal
 * @param {object} adminUser - Admin user object to edit
 * @param {function} onSave - Function to save changes
 * @param {boolean} loading - Loading state
 * @param {boolean} canChangeStatus - Whether current user can change active status (core_admin only)
 */
const EditAdminModal = ({ isOpen, onClose, adminUser, onSave, loading = false, canChangeStatus = false }) => {
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        name: '',
        password: '',
        is_active: 1
    });

    const [errors, setErrors] = useState({});
    const [showPasswordField, setShowPasswordField] = useState(false);
    const [emailChanged, setEmailChanged] = useState(false);

    // Initialize form data when adminUser changes
    useEffect(() => {
        if (adminUser) {
            setFormData({
                username: adminUser.username || '',
                email: adminUser.email || '',
                name: adminUser.name || '',
                password: '',
                is_active: adminUser.is_active ?? 1
            });
            setEmailChanged(false);
            setShowPasswordField(false);
            setErrors({});
        }
    }, [adminUser]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? (checked ? 1 : 0) : value;

        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // Track if email was changed
        if (name === 'email' && adminUser) {
            setEmailChanged(value !== adminUser.email);
        }

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Username validation
        if (!formData.username.trim()) {
            newErrors.username = 'Username is required';
        } else if (formData.username.length < 3) {
            newErrors.username = 'Username must be at least 3 characters';
        } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
            newErrors.username = 'Username can only contain letters, numbers, and underscores';
        }

        // Email validation
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        // Password validation (only if field is shown and has value)
        if (showPasswordField && formData.password) {
            if (formData.password.length < 6) {
                newErrors.password = 'Password must be at least 6 characters';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // Prepare data to send (only send changed fields)
        const dataToSend = {
            username: formData.username,
            email: formData.email,
            name: formData.name
        };

        // Only include password if it was changed
        if (showPasswordField && formData.password) {
            dataToSend.password = formData.password;
        }

        // Only include is_active if user can change status
        if (canChangeStatus) {
            dataToSend.is_active = formData.is_active;
        }

        await onSave(dataToSend);
    };

    const handleClose = () => {
        setFormData({
            username: '',
            email: '',
            name: '',
            password: '',
            is_active: 1
        });
        setErrors({});
        setShowPasswordField(false);
        setEmailChanged(false);
        onClose();
    };

    if (!adminUser) return null;

    const labelStyle = { display: 'block', marginBottom: '8px', fontWeight: '500' };
    const inputBaseStyle = {
        width: '100%',
        padding: '10px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        fontSize: '14px'
    };
    const fieldSpacingStyle = { marginBottom: '14px' };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={`Edit Admin User: ${adminUser.username}`}
            size="large"
        >
            <form onSubmit={handleSubmit} className="admin-user-edit-form">
                {/* Email Verification Status */}
                <div className="form-group" style={fieldSpacingStyle}>
                    <label style={labelStyle}>
                        Email Verification Status
                    </label>
                    <EmailVerificationBadge
                        emailVerifiedAt={adminUser.email_verified_at}
                        timezone={adminUser.org_timezone || 'UTC'}
                    />
                </div>

                <div className="form-grid">
                    {/* Username Field */}
                    <div className="form-group" style={fieldSpacingStyle}>
                        <label htmlFor="username" style={labelStyle}>
                            Username <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                            style={{
                                ...inputBaseStyle,
                                border: errors.username ? '1px solid #dc3545' : inputBaseStyle.border
                            }}
                            placeholder="Enter username"
                            disabled={loading}
                        />
                        {errors.username && (
                            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                                {errors.username}
                            </div>
                        )}
                    </div>

                    {/* Email Field */}
                    <div className="form-group" style={fieldSpacingStyle}>
                        <label htmlFor="email" style={labelStyle}>
                            Email <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            style={{
                                ...inputBaseStyle,
                                border: errors.email ? '1px solid #dc3545' : inputBaseStyle.border
                            }}
                            placeholder="Enter email"
                            disabled={loading}
                        />
                        {errors.email && (
                            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                                {errors.email}
                            </div>
                        )}
                    </div>
                </div>

                {emailChanged && (
                    <div style={{
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '4px',
                        padding: '10px',
                        marginTop: '-4px',
                        marginBottom: '16px',
                        fontSize: '13px',
                        color: '#856404'
                    }}>
                        <strong>Note:</strong> A verification email will be sent to the new email address.
                        The user must verify before they can use the new email for login.
                    </div>
                )}

                {/* Name Field */}
                <div className="form-group" style={fieldSpacingStyle}>
                    <label htmlFor="name" style={labelStyle}>
                        Full Name <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        style={{
                            ...inputBaseStyle,
                            border: errors.name ? '1px solid #dc3545' : inputBaseStyle.border
                        }}
                        placeholder="Enter full name"
                        disabled={loading}
                    />
                    {errors.name && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                            {errors.name}
                        </div>
                    )}
                </div>

                {/* Password Reset Section */}
                <div className="form-group" style={{ marginBottom: '14px' }}>
                    <div className="edit-admin-checkbox-row" style={{ marginBottom: '6px' }}>
                        <input
                            type="checkbox"
                            id="showPassword"
                            checked={showPasswordField}
                            onChange={(e) => {
                                setShowPasswordField(e.target.checked);
                                if (!e.target.checked) {
                                    setFormData(prev => ({ ...prev, password: '' }));
                                    setErrors(prev => ({ ...prev, password: null }));
                                }
                            }}
                            className="edit-admin-styled-checkbox"
                            disabled={loading}
                        />
                        <label htmlFor="showPassword" className="edit-admin-checkbox-label">
                            Reset Password
                        </label>
                    </div>

                    {showPasswordField && (
                        <>
                            <input
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                style={{
                                    ...inputBaseStyle,
                                    border: errors.password ? '1px solid #dc3545' : inputBaseStyle.border
                                }}
                                placeholder="Enter new password (min 6 characters)"
                                disabled={loading}
                            />
                            {errors.password && (
                                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>
                                    {errors.password}
                                </div>
                            )}
                            <div style={{
                                fontSize: '12px',
                                color: '#6c757d',
                                marginTop: '5px'
                            }}>
                                Leave blank to keep current password
                            </div>
                        </>
                    )}
                </div>

                {/* Active Status (Core Admin Only) */}
                {canChangeStatus && (
                    <div className="form-group" style={fieldSpacingStyle}>
                        <div className="edit-admin-checkbox-row">
                            <input
                                type="checkbox"
                                id="is_active"
                                name="is_active"
                                checked={formData.is_active === 1}
                                onChange={handleChange}
                                className="edit-admin-styled-checkbox"
                                disabled={loading}
                            />
                            <label htmlFor="is_active" className="edit-admin-checkbox-label">
                                Active Account
                            </label>
                        </div>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', marginLeft: '0' }}>
                            Inactive users cannot login to the system
                        </div>
                    </div>
                )}

                {/* Organization Info (Read-only) */}
                <div className="form-group" style={{
                    marginBottom: '20px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px'
                }}>
                    <div style={{ fontSize: '13px', color: '#6c757d', marginBottom: '8px' }}>
                        <strong>Organization:</strong> {adminUser.org_name || 'N/A'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#6c757d' }}>
                        <strong>User Type:</strong>{' '}
                        <span style={{
                            backgroundColor: adminUser.user_type === 'core_admin' ? '#007bff' : '#28a745',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: '3px',
                            fontSize: '11px'
                        }}>
                            {adminUser.user_type === 'core_admin' ? 'Core Admin' : 'Client Admin'}
                        </span>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions" style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '10px',
                    marginTop: '30px',
                    paddingTop: '20px',
                    borderTop: '1px solid #ddd'
                }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#6c757d',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                        }}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        style={{
                            padding: '10px 20px',
                            backgroundColor: '#007bff',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            opacity: loading ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                        disabled={loading}
                    >
                        {loading && <LoadingSpinner size="small" />}
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
            <style jsx>{`
                .admin-user-edit-form {
                    width: 100%;
                    max-width: 100%;
                    overflow-x: hidden;
                }

                .admin-user-edit-form .form-group {
                    min-width: 0;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(220px, 1fr));
                    gap: 16px;
                    align-items: start;
                }

                .form-grid .form-group {
                    margin-bottom: 0 !important;
                }

                .edit-admin-checkbox-row {
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    min-width: 0;
                    width: fit-content;
                    max-width: 100%;
                }

                .edit-admin-styled-checkbox {
                    width: 16px;
                    height: 16px;
                    margin: 0;
                    accent-color: #007bff;
                    cursor: pointer;
                    flex-shrink: 0;
                }

                .edit-admin-styled-checkbox:disabled {
                    cursor: not-allowed;
                    opacity: 0.65;
                }

                .edit-admin-checkbox-label {
                    display: inline-flex;
                    align-items: center;
                    cursor: pointer;
                    font-weight: 500;
                    margin: 0 !important;
                    padding: 0;
                    line-height: 1.2;
                    min-width: 0;
                }

                @media (max-width: 768px) {
                    .form-grid {
                        grid-template-columns: 1fr;
                        gap: 0;
                    }

                    .form-actions {
                        flex-direction: column-reverse;
                    }

                    .form-actions button {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>
        </Modal>
    );
};

export default EditAdminModal;
